import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components/macro';
import { Avatar } from '../../avatar/Avatar';
import { Icon } from '../../icon/Icon';
import { device } from '../../../Theme';
import selectedDot from '../../../../img/selectedDot.png';
import { Link } from 'react-router-dom';
import { rgba } from 'polished';

export const NavigationLink = ({
  avatar,
  avatarType,
  size,
  icon,
  iconType,
  active,
  customLabel,
  materialLabel,
  s4cLabel,
  s4cImageUrl,
  label,
  link,
  handleClick,
  alt,
  ...props
}) => {
  return (
    <LinkEl
      active={active.toString()}
      type={iconType}
      to={{ pathname: link }}
      onClick={handleClick}
      {...props}
    >
      {avatar && (
        <IconSpan>
          <Avatar active={active} size={size} type={avatarType} />
        </IconSpan>
      )}
      {icon && (
        <IconSpan>
          <Icon
            active={active}
            size={'x-large'}
            type={iconType}
            customLabel={customLabel}
            materialLabel={materialLabel}
            s4cLabel={s4cLabel}
            s4cImageUrl={s4cImageUrl}
            altText={""}
          />
        </IconSpan>
      )}
      <Label>{label}</Label>
    </LinkEl>
  );
};

const Label = styled.span`
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black90
      : props.theme.color.white90};
  margin: 0.5em 0;
`;

const IconSpan = styled.span`
  margin-right: 16px;
  @media ${device.tablet} {
    margin-right: 30px;
  }
`;

const LinkEl = styled(Link)`
  display: flex;
  position: relative;
  align-items: center;
  height: 44px;
  padding: 0 20px;
  ${(props) => props.theme.fontBook}
  font-size: 18px;
  font-weight: 400;
  cursor: pointer;
  line-height: 1;
  text-decoration: none;

  &:hover {
    box-shadow: none !important;
  }

  ${Label} {
    color: ${(props) =>
      props.theme.name === 'light'
        ? props.theme.color.black90
        : props.theme.color.white90};
  }

  ${IconSpan} span {
    opacity: 0.75;
  }
  ${IconSpan} img {
    ${(props) =>
      props.type === 's4c' &&
      `
      opacity: .8;
  `}
  }
  @media ${device.tablet} {
    padding: 0 30px;
  }
  ${({ active }) =>
    active === 'true' &&
    css`
      ${Label} {
        font-weight: 700;
      }
      &:before {
        position: absolute;
        left: -2px;
        width: 1em;
        height: 100%;
        background-image: url(${selectedDot});
        background-repeat: no-repeat;
        background-position-y: center;
        content: '';
      }
    `};

  &:hover {
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.blackFull
          : props.theme.color.whiteFull};
      font-weight: 700;
    }
    ${IconSpan} span,
    ${IconSpan} img {
      opacity: 1;
    }
  }

  &:active {
    box-shadow: 0px 0px 0px 2px
      ${(props) =>
        props.theme.name === 'light'
          ? `${rgba(props.theme.color.primaryFull, 0.2)}`
          : props.theme.color.primary10} !important;
    border-radius: 4px;
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black90
          : props.theme.color.white90};
    }
    ${IconSpan} span {
      opacity: 0.75;
    }
    ${IconSpan} img {
      ${(props) =>
        props.type === 's4c' &&
        `
        opacity: .8;
    `}
    }
  }

  &:focus-visible {
    box-shadow: 0px 0px 0px 2px
      ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.statusLightFocus
          : props.theme.color.statusDarkFocus} !important;
    border-radius: 4px;
    outline: none;
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.blackFull
          : props.theme.color.whiteFull};
    }
    ${IconSpan} span,
  ${IconSpan} img {
      opacity: 1;
    }
  }
  &:focus:not(:focus-visible) {
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black75
          : props.theme.color.white75};
    }
    outline: none;
  }

  &:disabled,
  &[disabled] {
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black75
          : props.theme.color.white75};
    }
    ${IconSpan} span,
  ${IconSpan} img {
      opacity: 0.5;
    }
  }
}
      opacity: 1;
    }
  }
  &:focus:not(:focus-visible) {
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black75
          : props.theme.color.white75};
    }
    outline: none;
  }

  &:disabled,
  &[disabled] {
    ${Label} {
      color: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black75
          : props.theme.color.white75};
    }
    ${IconSpan} span,
  ${IconSpan} img {
      opacity: 0.5;
    }
  }

`;

NavigationLink.propTypes = {
  disabled: PropTypes.bool,
  handleClick: PropTypes.func,
  label: PropTypes.string.isRequired,
  active: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large', 'x-large']),
  icon: PropTypes.bool,
  iconType: PropTypes.oneOf(['material', 's4c']),
  avatar: PropTypes.bool,
  avatarType: PropTypes.oneOf([
    'girl.png',
    'gorilla.png',
    'happy.png',
    'panda.png',
    'robot.png',
    'zombie.png',
  ]),
  materialLabel: PropTypes.oneOf([
    'remove_circle_outline',
    'control_point',
    'check_box',
    'check_box_outline_blank',
    'keyboard',
    'arrow_back_ios',
    'arrow_forward_ios',
    'share',
    'radio_button_checked',
    'search',
    'close',
    'home',
    'replay',
    'bookmark',
    'tv',
    'event',
    'layers',
    'navigate_before',
    'navigate_next',
  ]),
  s4cLabel: PropTypes.oneOf([
    'cyw',
    'avatar',
    'walter',
    'hansh',
    'learners',
    'stwnsh',
  ]),
};

NavigationLink.defaultProps = {
  disabled: false,
  handleClick: undefined,
  label: 'button',
  active: false,
  size: 'x-large',
  icon: false,
  iconType: 'material',
  avatar: false,
  avatarType: 'gorilla.png',
  materialLabel: 'home',
  s4cLabel: 'cyw',
};
